import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {preventativeRegistry} from "../../engine";
import {evaluateFieldSkip, isFieldSkippedForRow, isPreventativeFieldSkippable, resolveFieldSkipTarget, toggleSkipFieldCell,} from "../../engine/preventative-interactions/shared";

const hasTechnicalImportMeta = (issue) =>
    !!issue?.meta?.predefinedOptions
    || !!issue?.meta?.multiselectNotSupported
    || !!issue?.meta?.missingMappedProperties;

const mergePreventativeIssuesByRowIndex = (issues) => {
    if (!issues?.length) return [];
    const technicalMetaIssues = [];
    const mergeable = [];

    for (const issue of issues) {
        if (hasTechnicalImportMeta(issue)) {
            technicalMetaIssues.push(issue);
        } else {
            mergeable.push(issue);
        }
    }

    const map = new Map();
    for (const issue of mergeable) {
        const idx = issue.rowIndex;
        const incoming = [...(issue.invalidityCodes ?? [])];
        const prev = map.get(idx);
        if (!prev) {
            map.set(idx, {
                rowIndex: idx,
                originalRow: issue.originalRow,
                invalidityCodes: [...new Set(incoming)],
                meta: issue.meta,
            });
        } else {
            const merged = new Set([...(prev.invalidityCodes ?? []), ...incoming]);
            map.set(idx, {
                ...prev,
                invalidityCodes: [...merged],
                originalRow: issue.originalRow ?? prev.originalRow,
                meta: {...(prev.meta ?? {}), ...(issue.meta ?? {})},
            });
        }
    }

    return [...technicalMetaIssues, ...map.values()].sort((a, b) => a.rowIndex - b.rowIndex);
};

/**
 * @param {import('../../engine/preventative-interactions/shared/section-factories').PreventativeField} field
 * @param {Object} issue
 */
export const isPreventativeFieldActiveForIssue = (field, issue) =>
    !field.appliesToIssue || field.appliesToIssue(issue);

/**
 * @param {import('../../engine/preventative-interactions/shared/section-factories').PreventativeField} field
 */
export const isPreventativeFieldEditable = (field) =>
    field.type === 'input' ||
    field.type === 'toggle' ||
    (typeof field.type === 'string' && field.type.endsWith('-autocomplete'));

const toIgnoredRowIndexSet = (indices) =>
    new Set((indices ?? []).map((index) => Number(index)).filter((index) => !Number.isNaN(index)));

const normalizeSkippedFieldCells = (cells) =>
    (cells ?? []).map((cell) => ({
        rowIndex: Number(cell.rowIndex),
        fieldKey: cell.fieldKey,
    }));

const toSkipCellKeySet = (cells) =>
    new Set(normalizeSkippedFieldCells(cells).map((cell) => `${cell.rowIndex}:${cell.fieldKey}`));

const skipCellSetsEqual = (left, right) => {
    const leftKeys = toSkipCellKeySet(left);
    const rightKeys = toSkipCellKeySet(right);
    return leftKeys.size === rightKeys.size && [...leftKeys].every((key) => rightKeys.has(key));
};

const usePreventativeCorrection = ({
                                       open,
                                       parsedRows,
                                       preventativeIssues,
                                       selectedDataType,
                                       mappingFields = [],
                                       onSubmit,
                                       staticDataContext,
                                       userSkippedRowIndices = [],
                                       onUserSkippedRowIndicesChange,
                                       userSkippedFieldCells = [],
                                       t,
                                   }) => {
    const [patches, setPatches] = useState(new Map());
    const [skippedFieldCells, setSkippedFieldCells] = useState([]);
    const [baselineSkippedFieldCells, setBaselineSkippedFieldCells] = useState([]);
    const skippedFieldCellsRef = useRef(skippedFieldCells);
    skippedFieldCellsRef.current = skippedFieldCells;

    /**
     * Resets the session state to the initial values derived from user inputs. This is useful when reopening the dialog to ensure a clean slate for the user.
     * @type {(function(): void)|*}
     */
    const resetSession = useCallback(() => {
        const baseline = normalizeSkippedFieldCells(userSkippedFieldCells);
        setSkippedFieldCells(baseline);
        setBaselineSkippedFieldCells(baseline);
        setPatches(new Map());
    }, [userSkippedFieldCells]);

    /**
     * Resets the session state whenever the dialog is opened.
     */
    useEffect(() => {
        if (open) {
            resetSession();
        }
    }, [open, resetSession]);

    const ignoredRowIndices = useMemo(
        () => toIgnoredRowIndexSet(userSkippedRowIndices),
        [userSkippedRowIndices],
    );

    const mergedPreventativeIssues = useMemo(() => mergePreventativeIssuesByRowIndex(preventativeIssues), [preventativeIssues]);

    const sections = useMemo(() => {
        if (!selectedDataType?.id) return [];
        const configs = preventativeRegistry[selectedDataType.id] ?? [];

        return configs
            .map((config) => {
                const affectedIssues = mergedPreventativeIssues.filter((issue) =>
                    issue.invalidityCodes.includes(config.invalidityCode),
                );
                const fields =
                    typeof config.buildFields === 'function'
                        ? config.buildFields(affectedIssues, mappingFields)
                        : config.fields;

                return {
                    ...config,
                    fields: fields ?? [],
                    issues: affectedIssues,
                };
            })
            .filter((s) => s.issues.length > 0 && s.fields.length > 0);
    }, [selectedDataType, mergedPreventativeIssues, mappingFields]);

    /**
     * Updates the patch for a specific cell identified by rowIndex and fieldKey with the new value.
     * @type {(function(*, *, *): void)|*}
     */
    const onPatchRow = useCallback((rowIndex, fieldKey, newVal) => {
        setPatches((prev) => {
            const next = new Map(prev);
            const rowPatch = next.get(rowIndex) || {};
            next.set(rowIndex, {...rowPatch, [fieldKey]: newVal});
            return next;
        });
    }, []);

    /**
     * Toggles whether a specific row, identified by rowIndex, should be ignored (skipped) during the correction process. If the row is currently ignored, it will be unignored, and vice versa.
     * The updated set of ignored row indices is then communicated back through the onUserSkippedRowIndicesChange callback.
     * @type {(function(*): void)|*}
     */
    const toggleRowIgnored = useCallback((rowIndex) => {
        const next = new Set(ignoredRowIndices);
        if (next.has(rowIndex)) {
            next.delete(rowIndex);
        } else {
            next.add(rowIndex);
        }
        onUserSkippedRowIndicesChange?.([...next]);
    }, [ignoredRowIndices, onUserSkippedRowIndicesChange]);

    /**
     * Determines whether a specific row, identified by rowIndex, is currently marked as ignored (skipped) in the correction process.
     * This is done by checking if the rowIndex exists in the ignoredRowIndices set.
     * @type {function(*): boolean}
     */
    const isRowIgnored = useCallback((rowIndex) => ignoredRowIndices.has(rowIndex), [ignoredRowIndices]);

    /**
     * Determines whether a specific field, identified by fieldKey, in a given row (identified by rowIndex) is currently marked as skipped.
     * This is done by checking if the combination of rowIndex and fieldKey exists in the skippedFieldCells state, which tracks all the cells that have been marked as skipped by the user.
     * @type {function(*, *): *}
     */
    const isFieldSkipped = useCallback((rowIndex, fieldKey) => isFieldSkippedForRow(skippedFieldCells, rowIndex, fieldKey), [skippedFieldCells],);

    /**
     * Evaluates whether a specific field in a
     * given row can be skipped based on the current state of the correction process and the rules defined in the evaluateFieldSkip function.
     * @type {function(*, *): {allowed: boolean, reason}|{allowed: boolean, reason}|{allowed: boolean}}
     */
    const getSkipEvaluation = useCallback((field, issue) => {
        const parsedRow = parsedRows?.[issue.rowIndex] ?? issue.originalRow ?? {};
        return evaluateFieldSkip({
            selectedDataType,
            field,
            rowIndex: issue.rowIndex,
            parsedRow,
            skippedFieldCells,
            staticDataContext,
            t,
        });
    }, [parsedRows, selectedDataType, skippedFieldCells, staticDataContext, t],);

    /**
     * Toggles the skipped state of a specific field cell, identified by the combination of rowIndex and fieldKey. If the skip parameter is true, it attempts to mark the cell as skipped, but only if the evaluation allows it. If skip is false, it unmarks the cell as skipped.
     * The function updates the skippedFieldCells state accordingly to reflect the user's choice.
     * @type {(function(*, *, *): void)|*}
     */
    const toggleSkippedFieldCell = useCallback((rowIndex, fieldKey, skip) => {
        const field = sections
            .flatMap((section) => section.fields)
            .find((candidate) => resolveFieldSkipTarget(candidate) === fieldKey || candidate.key === fieldKey);

        if (!field) {
            return;
        }

        const issue = mergedPreventativeIssues.find((entry) => entry.rowIndex === rowIndex);
        if (!issue) {
            return;
        }

        if (skip) {
            const evaluation = evaluateFieldSkip({
                selectedDataType,
                field,
                rowIndex,
                parsedRow: parsedRows?.[rowIndex] ?? issue.originalRow ?? {},
                skippedFieldCells,
                staticDataContext,
                t,
            });
            if (!evaluation.allowed) {
                return;
            }
        }

        setSkippedFieldCells((prev) => toggleSkipFieldCell(prev, rowIndex, fieldKey, skip));
    }, [
        mergedPreventativeIssues,
        parsedRows,
        sections,
        selectedDataType,
        skippedFieldCells,
        staticDataContext,
        t,
    ]);

    /**
     * Determines the skip state for an entire column (field) within a specific section. It checks if the field is skippable and if there are any eligible issues in the section that would allow skipping.
     * Based on how many of those eligible issues are currently marked as skipped, it returns an object indicating whether the column is fully checked (all eligible issues are skipped), indeterminate (some eligible issues are skipped), or disabled (no eligible issues or field is not skippable).
     * @type {(function(*, *): ({checked: boolean, indeterminate: boolean, disabled: boolean}))|*}
     */
    const getColumnSkipState = useCallback((field, section) => {
        if (!field?.skippable || !section) {
            return {checked: false, indeterminate: false, disabled: true};
        }

        const skipTarget = resolveFieldSkipTarget(field);
        const eligibleIssues = section.issues.filter(
            (issue) =>
                !ignoredRowIndices.has(issue.rowIndex) &&
                isPreventativeFieldActiveForIssue(field, issue) &&
                isPreventativeFieldSkippable(field, issue),
        );

        if (!eligibleIssues.length) {
            return {checked: false, indeterminate: false, disabled: true};
        }

        const skippedCount = eligibleIssues.filter((issue) =>
            isFieldSkippedForRow(skippedFieldCells, issue.rowIndex, skipTarget),
        ).length;

        return {
            checked: skippedCount === eligibleIssues.length,
            indeterminate: skippedCount > 0 && skippedCount < eligibleIssues.length,
            disabled: false,
        };
    }, [ignoredRowIndices, skippedFieldCells]);

    /**
     * Toggles the skipped state for an entire column (field) within a specific section based on the skip parameter.
     * If skip is true, it attempts to mark all eligible issues in the section as skipped for that field, but only if the evaluation allows it for each issue.
     * If skip is false, it unmarks all those issues as skipped for that field.
     * @type {(function(*, *, *): void)|*}
     */
    const toggleColumnSkipForSection = useCallback((field, section, skip) => {
        if (!field?.skippable || !section) {
            return false;
        }

        const skipTarget = resolveFieldSkipTarget(field);
        const eligibleIssues = section.issues.filter(
            (issue) =>
                !ignoredRowIndices.has(issue.rowIndex) &&
                isPreventativeFieldActiveForIssue(field, issue) &&
                isPreventativeFieldSkippable(field, issue),
        );

        const prev = skippedFieldCellsRef.current;
        let nextCells = [...prev];

        if (skip) {
            for (const issue of eligibleIssues) {
                if (isFieldSkippedForRow(nextCells, issue.rowIndex, skipTarget)) {
                    continue;
                }
                const evaluation = evaluateFieldSkip({
                    selectedDataType,
                    field,
                    rowIndex: issue.rowIndex,
                    parsedRow: parsedRows?.[issue.rowIndex] ?? issue.originalRow ?? {},
                    skippedFieldCells: nextCells,
                    staticDataContext,
                    t,
                });
                if (evaluation.allowed) {
                    nextCells = toggleSkipFieldCell(nextCells, issue.rowIndex, skipTarget, true);
                }
            }
        } else {
            for (const issue of eligibleIssues) {
                nextCells = toggleSkipFieldCell(nextCells, issue.rowIndex, skipTarget, false);
            }
        }

        if (skipCellSetsEqual(prev, nextCells)) {
            return false;
        }

        setSkippedFieldCells(nextCells);
        return true;
    }, [
        ignoredRowIndices,
        parsedRows,
        selectedDataType,
        staticDataContext,
        t,
    ]);

    const skippedCellsChanged = useMemo(() => !skipCellSetsEqual(baselineSkippedFieldCells, skippedFieldCells), [baselineSkippedFieldCells, skippedFieldCells],);
    const canSubmit = patches.size > 0 || ignoredRowIndices.size > 0 || skippedCellsChanged;

    /**
     * Handles the submission of the preventative correction process.
     * It constructs the final set of patched rows by applying the user-made patches to the original parsed rows, while respecting any ignored rows and skipped fields.
     * @type {(function(): void)|*}
     */
    const handleSubmit = useCallback(() => {
        const configs = preventativeRegistry[selectedDataType.id] ?? [];

        const patchedRows = parsedRows.map((row, index) => {
            if (ignoredRowIndices.has(index)) {
                return row;
            }
            if (!patches.has(index)) {
                return row;
            }

            const rowPatches = patches.get(index);
            let updatedRow = {...row};
            const rowIssues = preventativeIssues.filter((issue) => issue.rowIndex === index);

            configs.forEach((section) => {
                const sectionFields =
                    typeof section.buildFields === 'function'
                        ? section.buildFields(
                            rowIssues.filter((issue) =>
                                issue.invalidityCodes?.includes(section.invalidityCode),
                            ),
                            mappingFields,
                        )
                        : section.fields;

                (sectionFields ?? []).forEach((field) => {
                    if (rowPatches[field.key] === undefined || field.type === 'text') {
                        return;
                    }

                    const matchingIssue = rowIssues.find(
                        (issue) =>
                            issue.invalidityCodes?.includes(section.invalidityCode) &&
                            isPreventativeFieldActiveForIssue(field, issue),
                    );

                    if (!matchingIssue) {
                        return;
                    }

                    const val = rowPatches[field.key];
                    const valueForModifyRow =
                        field.modifyRowUsesPatchValue === true
                            ? val
                            : typeof field.extractValue === 'function'
                                ? field.extractValue(val, matchingIssue)
                                : val;
                    if (typeof field.modifyRow === 'function') {
                        updatedRow = field.modifyRow(updatedRow, valueForModifyRow, matchingIssue);
                    }
                });
            });

            return updatedRow;
        });

        onSubmit({
            patchedRows,
            ignoredRowIndices: [...ignoredRowIndices],
            skippedFieldCells: normalizeSkippedFieldCells(skippedFieldCells),
        });
    }, [
        parsedRows,
        patches,
        ignoredRowIndices,
        selectedDataType,
        onSubmit,
        preventativeIssues,
        mappingFields,
        skippedFieldCells,
    ]);

    /**
     * Propagates a value change from a specific field in the current row to other rows within the same section that have the same issue, based on the specified scope ('all' or 'remaining').
     * @type {(function({currentSection: *, field: *, value: *}, *): void)|*}
     */
    const propagateRowValueToOthers = useCallback(({currentSection, field, value}, scope) => {
        const targets = currentSection.issues.filter(
            (issue) =>
                !ignoredRowIndices.has(issue.rowIndex) &&
                isPreventativeFieldActiveForIssue(field, issue),
        );

        switch (scope) {
            case 'all':
                setPatches((prev) => {
                    const next = new Map(prev);
                    for (const row of targets) {
                        const currentPatch = next.get(row.rowIndex);
                        next.set(row.rowIndex, {...(currentPatch || {}), [field.key]: value});
                    }
                    return next;
                });
                break;
            case 'remaining':
                setPatches((prev) => {
                    const next = new Map(prev);
                    for (const row of targets) {
                        const currentPatch = next.get(row.rowIndex);
                        if (!currentPatch || currentPatch[field.key] === undefined) {
                            next.set(row.rowIndex, {...(currentPatch || {}), [field.key]: value});
                        }
                    }
                    return next;
                });
                break;
            default:
                return;
        }
    }, [ignoredRowIndices]);

    return {
        sections,
        patches,
        ignoredRowIndices,
        skippedFieldCells,
        onPatchRow,
        toggleRowIgnored,
        isRowIgnored,
        isFieldSkipped,
        toggleSkippedFieldCell,
        getSkipEvaluation,
        getColumnSkipState,
        toggleColumnSkipForSection,
        propagateRowValueToOthers,
        handleSubmit,
        canSubmit,
        staticDataContext,
    };
};

export default usePreventativeCorrection;
